import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatTime } from "@/lib/api";

export function SlotPicker({ talentId, duration, value, onChange, initial, refreshKey = 0, compact = false, theme = "" }) {
  const ib = theme === "ib";
  const [slots, setSlots] = useState(null);
  const [dayIdx, setDayIdx] = useState(0);

  useEffect(() => {
    setSlots(null);
    if (!duration) return;
    let alive = true;
    api.get(`/talents/${talentId}/slots`, { params: { duration } }).then((r) => {
      if (!alive) return;
      setSlots(r.data);
      const all = r.data.days.flatMap((d) => d.slots.map((s) => s.start));
      let pick = null;
      if (initial === "now" && r.data.now_available) pick = "now";
      else if (initial && all.includes(initial)) pick = initial;
      else if (initial) toast.error("Slot yang kamu pilih sudah tidak tersedia, pilih slot lain");
      onChange(pick);
      const idx = pick && pick !== "now" ? r.data.days.findIndex((d) => d.slots.some((s) => s.start === pick)) : r.data.days.findIndex((d) => d.slots.length > 0);
      setDayIdx(idx >= 0 ? idx : 0);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId, duration, refreshKey]);

  if (!duration) return null;
  if (!slots) return <p className={`mt-2 text-sm ${ib ? "text-[#626262]" : "text-slate-500"}`} data-testid="slots-loading">Memuat slot...</p>;
  const noneAtAll = slots.days.every((d) => !d.slots.length) && !slots.now_available;
  if (noneAtAll) {
    return ib ? (
      <div className="ib-empty" data-testid="slot-empty">
        <p className="font-semibold text-black">Jadwal pada talent ini sedang tidak ada</p>
        <Link to="/talent" className="ib-btn mt-5" data-testid="slot-empty-cta">Lihat Talent Lain</Link>
      </div>
    ) : (
      <div className="mt-3 rounded-xl border border-border bg-muted px-5 py-8 text-center" data-testid="slot-empty">
        <p className="font-medium text-slate-700">Jadwal pada talent ini sedang tidak ada</p>
        <p className="mt-1 text-sm text-slate-500">Belum ada slot kosong 7 hari ke depan. Coba durasi lain atau talent lain.</p>
      </div>
    );
  }
  const day = slots.days[dayIdx];
  const dayCls = (active) => ib
    ? `ib-outline shrink-0 ${active ? "active" : ""}`
    : `shrink-0 px-3 h-10 rounded-full text-sm border transition-colors duration-300 disabled:opacity-40 ${active ? "bg-slate-800 text-white border-slate-800" : "bg-card border-border text-slate-600 hover:bg-secondary"}`;
  const slotCls = (active) => ib
    ? `ib-outline ${active ? "!bg-[#0B4F7A] !text-white !border-[#0B4F7A]" : ""}`
    : `h-10 rounded-lg text-sm border transition-colors duration-300 ${active ? "bg-primary text-white border-primary" : "bg-card border-border text-slate-700 hover:border-primary"}`;
  return (
    <div data-testid="booking-slot-picker">
      {slots.now_available && (
        <button type="button" onClick={() => onChange("now")} className={ib ? `ib-outline mt-2 mr-2 rounded-full ${value === "now" ? "!bg-[#0B4F7A] !text-white !border-[#0B4F7A]" : "!border-[#0B4F7A] !text-[#0B4F7A]"}` : `mt-2 mr-2 px-4 h-10 rounded-full text-sm border transition-colors duration-300 ${value === "now" ? "bg-primary text-white border-primary" : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"}`} data-testid="slot-now">
          Sekarang — talent online
        </button>
      )}
      <div className="mt-3 flex gap-2 pb-2 flex-wrap" data-testid="slot-days">
        {slots.days.map((d, i) => (
          <button type="button" key={d.date} disabled={!d.slots.length} onClick={() => setDayIdx(i)} className={dayCls(dayIdx === i)} data-testid={`slot-day-${d.date}`}>
            {d.label} <span className="text-xs opacity-70">{d.day_off ? "(libur)" : `(${d.slots.length})`}</span>
          </button>
        ))}
      </div>
      {day?.slots.length ? (
        <div className={`mt-3 grid gap-2 ${compact ? "grid-cols-4 sm:grid-cols-6 lg:grid-cols-8" : "grid-cols-4 sm:grid-cols-6"}`} data-testid="slot-grid">
          {day.slots.map((s) => (
            <button type="button" key={s.start} onClick={() => onChange(s.start)} className={slotCls(value === s.start)} data-testid={`slot-${s.label.replace(":", "")}`}>{s.label}</button>
          ))}
        </div>
      ) : (
        <p className={`mt-3 text-sm ${ib ? "text-[#626262]" : "text-slate-500"}`} data-testid="slot-day-empty">Tidak ada slot di hari ini.</p>
      )}
      {value && <p className={`mt-3 text-sm ${ib ? "text-black" : "text-slate-700"}`} data-testid="slot-selected">Dipilih: <span className="font-semibold">{value === "now" ? "Sekarang" : formatTime(value) + " WIB"}</span></p>}
    </div>
  );
}
